import React, { useState, useEffect } from 'react';
import { Download, Filter, X } from 'lucide-react';
import { AdminLayout, AdminTable, AdminPagination } from '../../components/admin';
import { Button, Input } from '../../components/ui';
import { toast } from '../../components/ui/Toaster';
import { AdminPaymentService, PaymentFilters, PaymentWithUser } from '../../services/adminPaymentService';

const PaymentManagementPage: React.FC = () => {
  const [payments, setPayments] = useState<PaymentWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Filter states
  const [filters, setFilters] = useState<PaymentFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  // Load payments
  const loadPayments = async (page: number = 1) => {
    try {
      setLoading(true);
      const currentFilters: PaymentFilters = {};

      if (dateFrom) currentFilters.dateFrom = dateFrom;
      if (dateTo) currentFilters.dateTo = dateTo;
      if (status) currentFilters.status = status as any;
      if (search.trim()) currentFilters.search = search.trim();

      const result = await AdminPaymentService.getPayments(currentFilters, { page, limit: pagination.limit });

      setPayments(result.payments);
      setPagination(result.pagination);
      setFilters(currentFilters);
    } catch (error) {
      console.error('Failed to load payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      setExporting(true);
      await AdminPaymentService.exportPayments(filters);
      toast.success('Payments exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export payments');
    } finally {
      setExporting(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setStatus('');
    setSearch('');
  };

  // Apply filters
  const applyFilters = () => {
    loadPayments(1);
    setShowFilters(false);
  };

  // Format feature type for display
  const formatFeatureType = (featureType: string): string => {
    const featureMap: { [key: string]: string } = {
      'extra_group_slots': 'Extra Group Slots',
      'extended_group_size': 'Group Size Extension',
      'chat_enhancements': 'Chat Enhancements'
    };
    return featureMap[featureType] || featureType;
  };

  // Format status badge
  const getStatusBadge = (status: string) => {
    const statusMap = {
      'completed': { className: 'sf-badge-lime', label: 'Completed' },
      'pending': { className: 'sf-badge-cyan', label: 'Pending' },
      'failed': { className: 'sf-badge-pink', label: 'Failed' },
      'refunded': { className: 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-muted', label: 'Refunded' }
    };

    const style = statusMap[status as keyof typeof statusMap] || statusMap.pending;

    return (
      <span className={style.className}>
        {style.label}
      </span>
    );
  };

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      render: (_value: any, payment: PaymentWithUser) => `#${payment.id}`
    },
    {
      key: 'createdAt',
      title: 'Date',
      render: (_value: any, payment: PaymentWithUser) => {
        const date = new Date(payment.createdAt);
        return date.toLocaleDateString('nl-NL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    {
      key: 'user',
      title: 'User',
      render: (_value: any, payment: PaymentWithUser) => (
        <div>
          <div className="font-medium text-fg-bright">{payment.user?.username || 'N/A'}</div>
          <div className="text-sm text-fg-muted">{payment.user?.email || 'N/A'}</div>
        </div>
      )
    },
    {
      key: 'featureType',
      title: 'Feature',
      render: (_value: any, payment: PaymentWithUser) => formatFeatureType(payment.featureType)
    },
    {
      key: 'amount',
      title: 'Amount',
      render: (_value: any, payment: PaymentWithUser) => `€${(payment.amount || 0).toFixed(2)}`
    },
    {
      key: 'quantity',
      title: 'Quantity',
      render: (_value: any, payment: PaymentWithUser) => `${payment.quantity || 1}x`
    },
    {
      key: 'status',
      title: 'Status',
      render: (_value: any, payment: PaymentWithUser) => getStatusBadge(payment.status)
    },
    {
      key: 'paymentId',
      title: 'Payment ID',
      render: (_value: any, payment: PaymentWithUser) => payment.paymentId || '-'
    }
  ];

  // Load initial data
  useEffect(() => {
    loadPayments();
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <div className="sf-eyebrow mb-3">// admin</div>
            <h1 className="text-3xl font-bold tracking-tight text-fg-bright">Payment Management</h1>
            <p className="mt-2 text-sm text-fg-muted">View and export all premium purchase transactions</p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="secondary"
              size="sm"
              className="flex items-center space-x-2"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              size="sm"
              className="flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>{exporting ? 'Exporting...' : 'Export CSV'}</span>
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="sf-card p-4">
            <div className="sf-label-mono">Total Transactions</div>
            <div className="mt-1 text-2xl font-bold text-fg-bright">{pagination.total}</div>
          </div>
          <div className="sf-card p-4">
            <div className="sf-label-mono">Current Page</div>
            <div className="mt-1 text-2xl font-bold text-fg-bright">{pagination.page} of {pagination.totalPages}</div>
          </div>
          <div className="sf-card p-4">
            <div className="sf-label-mono">Showing</div>
            <div className="mt-1 text-2xl font-bold text-fg-bright">{payments.length} records</div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="sf-card p-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-fg-bright">Filters</h3>
              <Button
                onClick={() => setShowFilters(false)}
                variant="ghost"
                size="sm"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Date From */}
              <div>
                <label className="block text-sm font-semibold text-fg-soft mb-1.5">
                  Date From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="sf-input py-2.5 text-sm"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-semibold text-fg-soft mb-1.5">
                  Date To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="sf-input py-2.5 text-sm"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-fg-soft mb-1.5">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="sf-input py-2.5 text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="PENDING">Pending</option>
                  <option value="FAILED">Failed</option>
                  <option value="REFUNDED">Refunded</option>
                </select>
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-semibold text-fg-soft mb-1.5">
                  Search
                </label>
                <Input
                  placeholder="Username, email, or payment ID"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {/* Note: the shared Input component renders no icon prop, so the
                    previous icon={Search} was a no-op and has been removed. */}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                onClick={clearFilters}
                variant="secondary"
                size="sm"
              >
                Clear All
              </Button>
              <Button
                onClick={applyFilters}
                size="sm"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Payments Table */}
        <div>
          <AdminTable
            data={payments}
            columns={columns}
            loading={loading}
            emptyMessage="No payments found"
          />

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4">
              <AdminPagination
                pagination={pagination}
                onPageChange={(page) => loadPayments(page)}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default PaymentManagementPage;
